import { Box, Divider, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  useRestoreLatestSnapshotMutation,
  useSaveDataSnapshotMutation,
} from "../../../graphql/generated";
import useDialog from "../../../utils/hooks/useDialog";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import OrgDataTransferContainer from "../../demo-utils/OrgDataTransfer";
import { testIds } from "./DemoUtils.testIds";

const DemoUtils = () => {
  const { t } = useTranslation(["settings", "common"]);
  const saveSnapshotMutation = useSaveDataSnapshotMutation();
  const restoreSnapshotMutation = useRestoreLatestSnapshotMutation();
  const [isSavingSnapshot, setIsSavingSnapshot] = useState(false);
  const [isRestoringSnapshot, setIsRestoringSnapshot] = useState(false);

  const { showDialog, hideDialog } = useDialog();
  return (
    <Stack direction="column" spacing={4}>
      {/* Snapshot Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          {t("demoUtils.snapshots", "Data Snapshots")}
        </Typography>
        <Stack direction="column" alignItems="center" spacing={2}>
          <LoadingButton data-testid={testIds.demoUtilsSaveSnapshot}
            variant="contained"
            loading={isSavingSnapshot}
            onClick={() => {
              showDialog({
                type: "primary",
                title: t("demoUtils.saveSnapshot", "Save snapshot"),
                description: t(
                  "demoUtils.saveSnapshotConfirm",
                  "Are you sure you want to save a snapshot of the current data?"
                ),
                actions: [
                  {
                    title: t("cancel", "Cancel"),
                    type: "secondary",
                  },
                  {
                    title: t("save", "Save"),
                    type: "primary",
                    onClick: async () => {
                      try {
                        setIsSavingSnapshot(true);
                        hideDialog();
                        await saveSnapshotMutation.mutateAsync({});
                        showDialog({
                          type: "primary",
                          title: t("success", "Success"),
                          description: t(
                            "demoUtils.snapshotSuccess",
                            "Snapshot saved successfully"
                          ),
                        });
                      } catch (error) {
                        showDialog({
                          type: "error",
                          title: t("common:error.title", "Error"),
                          description:
                            "An error occurred while saving the snapshot. " +
                            (error as Error).message,
                        });
                      } finally {
                        setIsSavingSnapshot(false);
                      }
                    },
                  },
                ],
              });
            }}
          >
            {t("demoUtils.saveSnapshot", "Save snapshot")}
          </LoadingButton>
          <LoadingButton data-testid={testIds.demoUtilsRestoreSnapshot}
            variant="outlined"
            loading={isRestoringSnapshot}
            onClick={() => {
              showDialog({
                type: "primary",
                title: t("demoUtils.restoreSnapshot", "Restore snapshot"),
                description: t(
                  "demoUtils.restoreSnapshotConfirm",
                  "Are you sure you want to restore the latest snapshot?. This will reset your data to the state it was when the snapshot was taken."
                ),
                actions: [
                  {
                    title: t("cancel", "Cancel"),
                    type: "secondary",
                  },
                  {
                    title: t("restore", "Restore"),
                    type: "primary",
                    onClick: async () => {
                      try {
                        setIsRestoringSnapshot(true);
                        hideDialog();
                        await restoreSnapshotMutation.mutateAsync({});
                        showDialog({
                          type: "primary",
                          title: t("success", "Success"),
                          description: t(
                            "demoUtils.restoreSuccess",
                            "Snapshot restored successfully"
                          ),
                        });
                      } catch (error) {
                        showDialog({
                          type: "error",
                          title: t("common:error.title", "Error"),
                          description:
                            t(
                              "demoUtils.error.generic",
                              "An error occurred while restoring the snapshot. "
                            ) + (error as Error).message,
                        });
                      } finally {
                        setIsRestoringSnapshot(false);
                      }
                    },
                  },
                ],
              });
            }}
          >
            {t("demoUtils.restoreSnapshot", "Restore snapshot")}
          </LoadingButton>
        </Stack>
      </Box>

      <Divider />

      {/* Organization Data Transfer Section */}
      <Box>
        <OrgDataTransferContainer />
      </Box>
    </Stack>
  );
};

export default DemoUtils;
